//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import moment from "moment";

const dailyUtcCronPattern = /^(\d{1,2})\s+(\d{1,2})\s+\*\s+\*\s+\*$/;

export interface DailyUtcCron {
  minute: number;
  hour: number;
}

export function parseDailyUtcCron(expr: string): DailyUtcCron | undefined {
  const match = (expr || "").trim().match(dailyUtcCronPattern);
  if (!match) {
    return undefined;
  }
  const minute = parseInt(match[1], 10);
  const hour = parseInt(match[2], 10);
  if (Number.isNaN(minute) || Number.isNaN(hour) || minute < 0 || minute > 59 || hour < 0 || hour > 23) {
    return undefined;
  }
  return { minute, hour };
}

export function formatDailyUtcCron(parsed: DailyUtcCron): string {
  return `${parsed.minute} ${parsed.hour} * * *`;
}

export function formatUtcTimeLabel(expr: string): string {
  const parsed = parseDailyUtcCron(expr);
  if (!parsed) {
    return expr;
  }
  const hh = String(parsed.hour).padStart(2, "0");
  const mm = String(parsed.minute).padStart(2, "0");
  return `${hh}:${mm} UTC`;
}

export function dailyUtcCronToLocalTime(expr: string): string {
  const parsed = parseDailyUtcCron(expr);
  if (!parsed) {
    return "";
  }
  return moment.utc().hour(parsed.hour).minute(parsed.minute).second(0).millisecond(0).local().format("HH:mm");
}

export function localTimeToDailyUtcCron(localTime: string): string | undefined {
  const match = (localTime || "").trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return undefined;
  }
  const hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  if (Number.isNaN(hour) || Number.isNaN(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return undefined;
  }
  const utc = moment().hour(hour).minute(minute).second(0).millisecond(0).utc();
  return formatDailyUtcCron({ hour: utc.hour(), minute: utc.minute() });
}

export function localTimezoneLabel(): string {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offset = moment().format("Z");
  if (tz) {
    return `${tz} (UTC${offset})`;
  }
  return `UTC${offset}`;
}

export function isDailyUtcCron(expr: string): boolean {
  return parseDailyUtcCron(expr) !== undefined;
}
